import { useState } from "react";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { TESTIMONIALS } from "@/data/site";
import GoogleBadge from "@/components/GoogleBadge";

export default function Testimonials() {
  const [i, setI] = useState(0);
  const total = TESTIMONIALS.length;
  const prev = () => setI((p) => (p - 1 + total) % total);
  const next = () => setI((p) => (p + 1) % total);
  const t = TESTIMONIALS[i];

  return (
    <section
      id="voices"
      data-testid="testimonials-section"
      className="relative py-20 md:py-40 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 reveal">
          <div>
            <p className="eyebrow">03 — Voices</p>
            <h2 className="font-display text-white font-light text-4xl md:text-6xl tracking-tighter mt-4 leading-[1.05]">
              What our <span className="gold-text italic">clients</span> say.
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <GoogleBadge />
            <div className="w-px h-8 bg-white/10 hidden sm:block mx-1" />
            <button
              data-testid="testimonial-prev"
              onClick={prev}
              aria-label="Previous testimonial"
              className="w-12 h-12 border border-white/10 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37] transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid="testimonial-next"
              onClick={next}
              aria-label="Next testimonial"
              className="w-12 h-12 border border-white/10 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37] transition"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8 relative">
            <Quote className="w-14 h-14 text-[#D4AF37]/40 mb-6" />
            <blockquote
              key={i}
              data-testid="testimonial-active"
              className="font-serif text-white text-2xl md:text-4xl leading-[1.25] tracking-tight italic"
              style={{ animation: "fadeUp 0.7s ease both" }}
            >
              “{t.text}”
            </blockquote>
            <div className="mt-10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] font-display text-lg">
                {t.name.charAt(0)}
              </div>
              <div>
                <p className="text-white text-sm font-medium tracking-wide">
                  {t.name}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {Array.from({ length: 5 }).map((_, n) => (
                    <Star
                      key={n}
                      className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-4">
            {TESTIMONIALS.map((tt, idx) => (
              <button
                key={tt.name}
                data-testid={`testimonial-pick-${idx}`}
                onClick={() => setI(idx)}
                className={`w-full text-left p-5 border transition-all duration-500 ${
                  idx === i
                    ? "border-[#D4AF37]/60 bg-[#D4AF37]/5"
                    : "border-white/8 hover:border-white/25"
                }`}
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Client 0{idx + 1}
                </p>
                <p className="font-display text-white text-lg mt-2">
                  {tt.name}
                </p>
                <p className="text-white/45 text-xs mt-2 line-clamp-2">
                  {tt.text}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-16 reveal">
          <div className="gold-line" />
          <div className="overflow-hidden mt-10">
            <div className="marquee whitespace-nowrap">
              {Array.from({ length: 2 }).flatMap((_, dup) =>
                [
                  "Paint Correction",
                  "Ceramic Coating",
                  "Interior Restoration",
                  "Vinyl Wraps",
                  "Mobile Service",
                  "Pukekohe NZ",
                  "Family Owned",
                ].map((label, idx) => (
                  <span
                    key={`${dup}-${idx}`}
                    className="font-display text-5xl md:text-7xl font-light tracking-tighter px-8 text-white/10 hover:text-[#D4AF37]/60 transition-colors"
                  >
                    {label} <span className="text-[#D4AF37]/60">✦</span>
                  </span>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
